import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCardIcon, 
  TrashIcon, 
  CheckCircleIcon,
  PlusIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { usePaymentMethods } from '../../../../hooks/usePaymentMethods';
import { 
  formatCardBrand, 
  getCardBrandIcon, 
  getCardBrandColor,
  formatExpiryDate,
  getCardExpiryStatus,
  sortPaymentMethods
} from '../../../../utils/paymentUtils';

interface SavedPaymentMethodsProps {
  onSelectPaymentMethod?: (paymentMethodId: string) => void;
  onAddNewCard?: () => void;
  selectedPaymentMethodId?: string;
  showAddButton?: boolean;
}

const SavedPaymentMethods: React.FC<SavedPaymentMethodsProps> = ({
  onSelectPaymentMethod,
  onAddNewCard,
  selectedPaymentMethodId,
  showAddButton = true
}) => {
  const { 
    paymentMethods, 
    loading, 
    error, 
    deletePaymentMethod, 
    setDefaultPaymentMethod,
    refreshPaymentMethods,
    clearError
  } = usePaymentMethods();
  
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);

  // Sort payment methods for display
  const sortedPaymentMethods = sortPaymentMethods(paymentMethods);

  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta tarjeta?')) {
      return;
    }

    setDeletingId(paymentMethodId);
    const success = await deletePaymentMethod(paymentMethodId);
    setDeletingId(null);
    
    if (!success) {
      // Error is handled by the context
      console.error('Failed to delete payment method');
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId: string) => {
    setSettingDefaultId(paymentMethodId);
    const success = await setDefaultPaymentMethod(paymentMethodId);
    setSettingDefaultId(null);
    
    if (!success) {
      // Error is handled by the context
      console.error('Failed to set default payment method');
    }
  };

  const getExpiryStatusBadge = (expMonth: number, expYear: number) => {
    const status = getCardExpiryStatus(expMonth, expYear);
    
    switch (status) {
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
            <ExclamationTriangleIcon className="w-3 h-3" />
            Expirada
          </span>
        );
      case 'expiring_soon':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
            <ExclamationTriangleIcon className="w-3 h-3" />
            Expira pronto
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse">
            <div className="bg-gray-200 rounded-2xl h-20"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
        <p className="text-red-600 text-sm">{error}</p>
        <div className="flex gap-2 mt-2">
          <button
            onClick={refreshPaymentMethods}
            className="text-red-600 hover:text-red-700 text-sm font-medium"
          >
            Reintentar
          </button>
          <button
            onClick={clearError}
            className="text-red-600 hover:text-red-700 text-sm font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Tarjetas Guardadas
        </h3>
        {showAddButton && (
          <button
            onClick={onAddNewCard}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-400 hover:bg-yellow-500 
                     text-black rounded-full font-medium transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Añadir Tarjeta
          </button>
        )}
      </div>

      {sortedPaymentMethods.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-2xl">
          <CreditCardIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No tienes tarjetas guardadas</p>
          {showAddButton && (
            <button
              onClick={onAddNewCard}
              className="px-6 py-2 bg-yellow-400 hover:bg-yellow-500 
                       text-black rounded-full font-medium transition-colors"
            >
              Añadir Primera Tarjeta
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {sortedPaymentMethods.map((paymentMethod) => (
              <motion.div
                key={paymentMethod.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`
                  relative bg-white border-2 rounded-2xl p-4 cursor-pointer transition-all
                  ${selectedPaymentMethodId === paymentMethod.id
                    ? 'border-yellow-400 bg-yellow-50'
                    : 'border-gray-200 hover:border-gray-300'
                  }
                  ${deletingId === paymentMethod.id ? 'opacity-50' : ''}
                `}
                onClick={() => onSelectPaymentMethod?.(paymentMethod.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">
                      {getCardBrandIcon(paymentMethod.brand)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-medium ${getCardBrandColor(paymentMethod.brand)}`}>
                          {formatCardBrand(paymentMethod.brand)} •••• {paymentMethod.last4}
                        </span>
                        {paymentMethod.isDefault && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 
                                         bg-green-100 text-green-700 text-xs rounded-full">
                            <CheckCircleIcon className="w-3 h-3" />
                            Por defecto
                          </span>
                        )}
                        {getExpiryStatusBadge(paymentMethod.expMonth, paymentMethod.expYear)}
                      </div>
                      <p className="text-sm text-gray-600">
                        Expira {formatExpiryDate(paymentMethod.expMonth, paymentMethod.expYear)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!paymentMethod.isDefault && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetDefaultPaymentMethod(paymentMethod.id);
                        }}
                        disabled={settingDefaultId === paymentMethod.id}
                        className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 
                                 border border-gray-300 hover:border-gray-400 rounded-full
                                 transition-colors disabled:opacity-50"
                      >
                        {settingDefaultId === paymentMethod.id ? 'Configurando...' : 'Hacer por defecto'}
                      </button>
                    )}
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePaymentMethod(paymentMethod.id);
                      }}
                      disabled={deletingId === paymentMethod.id}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors
                               disabled:opacity-50"
                      title="Eliminar tarjeta"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {selectedPaymentMethodId === paymentMethod.id && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute inset-0 border-2 border-yellow-400 rounded-2xl pointer-events-none"
                  />
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default SavedPaymentMethods;